
import React, { createContext, useState, useEffect } from 'react';
import { Language, languageOptions, translations, TranslationType } from '../translations';

export type LanguageContextType = {
  language: Language;
  setLanguage: (language: Language) => void;
  showLanguageDialog: boolean;
  setShowLanguageDialog: (show: boolean) => void;
  showWelcomeDialog: boolean;
  setShowWelcomeDialog: (show: boolean) => void;
  t: TranslationType;
};

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Maps the browser locale (navigator.language, e.g. "de-AT") to a supported app language.
const detectBrowserLanguage = (): Language | null => {
  if (typeof navigator === 'undefined') return null;
  const codeMap: Record<string, Language> = {
    en: 'english',
    it: 'italian',
    de: 'german',
    fr: 'french',
    es: 'spanish',
    pl: 'polish',
    pt: 'portuguese',
  };
  const locales = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const locale of locales) {
    const base = locale.toLowerCase().split('-')[0];
    const match = codeMap[base];
    if (match && languageOptions.some((option) => option.value === match)) return match;
  }
  return null;
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  // Read the explicit user choice synchronously so it always wins over the
  // browser locale and is never overwritten by a later effect.
  const getSavedLanguage = (): Language | null => {
    try {
      const saved = localStorage.getItem('preferred-language');
      if (saved && languageOptions.some(option => option.value === saved)) return saved as Language;
    } catch { /* storage unavailable */ }
    return null;
  };

  const savedLanguage = typeof window !== 'undefined' ? getSavedLanguage() : null;

  const [language, setLanguageState] = useState<Language>(
    savedLanguage ?? detectBrowserLanguage() ?? 'english'
  );
  // No explicit choice yet -> ask the user, with the detected locale preselected.
  const [showLanguageDialog, setShowLanguageDialog] = useState(!savedLanguage);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  // Get translations for current language
  const t = translations[language] as TranslationType;

  // Every language change is an explicit user choice: persist it immediately.
  const setLanguage = (next: Language) => {
    setLanguageState(next);
    try {
      localStorage.setItem('preferred-language', next);
    } catch { /* storage unavailable */ }
  };

  // Keep <html lang> in sync for accessibility and browser translation hints.
  useEffect(() => {
    const htmlLang: Record<Language, string> = {
      english: 'en', italian: 'it', german: 'de', spanish: 'es',
      french: 'fr', polish: 'pl', portuguese: 'pt',
    };
    document.documentElement.lang = htmlLang[language] || 'en';
  }, [language]);

  // Closing the language dialog confirms the currently selected language.
  useEffect(() => {
    if (!showLanguageDialog) {
      try {
        localStorage.setItem('preferred-language', language);
      } catch { /* storage unavailable */ }
      const hasSeenWelcome = localStorage.getItem('has-seen-welcome');
      if (!hasSeenWelcome) {
        setShowWelcomeDialog(true);
      }
    }
  }, [showLanguageDialog]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      showLanguageDialog, 
      setShowLanguageDialog, 
      showWelcomeDialog, 
      setShowWelcomeDialog,
      t 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
